import { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';

/**
 * Hook to dynamically fetch option prices for a sauna model from the database
 * @param {string} saunaId - The sauna model ID (e.g., 'umi', 'hiro', 'mori')
 * @returns {object} { pricesMap, loading, error }
 * pricesMap is a record of optionId -> price for all options of this sauna
 */
export function useSaunaOptionPrices(saunaId) {
  const [pricesMap, setPricesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPrices = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all ConfigurationPrice entries for this sauna
        const allPrices = await base44.entities.ConfigurationPrice.filter({ saunaId });
        
        const map = {};
        allPrices.forEach(p => {
          // Key by sectionId_optionId to avoid collisions across sections
          const key = p.sectionId ? `${p.sectionId}__${p.optionId}` : p.optionId;
          map[key] = p.price;
        });
        setPricesMap(map);
      } catch (err) {
        console.error(`Error loading prices for sauna "${saunaId}":`, err);
        setError(err.message);
        setPricesMap({});
      } finally {
        setLoading(false);
      }
    };

    if (saunaId) {
      loadPrices();

      // Subscribe to ConfigurationPrice changes for real-time updates
      const unsubscribe = base44.entities.ConfigurationPrice.subscribe(() => {
        loadPrices();
      });

      return () => {
        unsubscribe();
      };
    }
  }, [saunaId]);

  return { pricesMap, loading, error };
}